import React, { useEffect, useState, Fragment } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import Select from 'react-select';
import { ChevronRightIcon, PlusSmallIcon, PencilSquareIcon, XMarkIcon, UserIcon } from '@heroicons/react/24/outline';
import Header from './common/Header';
import Footer from './common/Footer';
import config from '../config';
import { getData, notify, postData } from '../functions';


const AllUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [userEditOpen, setUserEditOpen] = useState(false);
  const [userEditInfo, setUserEditInfo] = useState(null);
  const [practiceOptions, setPracticeOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [passwords, setPasswords] = useState({
    password: '',
    confirm_password: '',
    matching: true,
    confirmTouched: false,
  });
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
  
    if (name === 'password') {
      setPasswords({
        ...passwords,
        [name]: value,
      });
    } else if (name === 'confirm_password') {
      const passwordsMatch = passwords.password === value;
      setPasswords({
        ...passwords,
        [name]: value,
        matching: passwordsMatch && passwords.confirmTouched,
      });
    } else {
      setPasswords({
        ...passwords,
        [name]: value,
      });
    }
  
    if (name === 'confirm_password' && !passwords.confirmTouched) {
      setPasswords({
        ...passwords,
        confirmTouched: true,
      });
    }
  };
  
  const passwordsMatch = passwords.password === passwords.confirm_password;
  
  const passwordClasses = `block outline-0 w-full rounded-md border-0 p-1.5 px-3 shadow-sm ring-1 ring-inset ${
    passwords.matching || (!passwords.password && !passwords.confirm_password)
      ? 'ring-gray-300 focus:ring-indigo-500 dark:border-gray-50 dark:placeholder-gray-400 dark:focus:ring-indigo-500 dark:focus:border-indigo-500'
      : 'ring-red-500'
  } placeholder:text-gray-400 focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`;

  const fetchData = async () => {
      try {
        const result = await getData(`${config.chartServerUrl}/all/users`, true);
        if (result) {
          setAllUsers(result);
        }
      } catch (error) {
        notify('error', 'Something went wrong');
      }
    };


  useEffect(() => {
    
    fetchData();
    getPracticeList();
    getRoleList();
  }, []);

  const getPracticeList = async () => {
    const result = await getData(`${config.chartServerUrl}/all/practice`, true);
    if (result) {
      const data = result.map((items) => ({
        value: items.practice_id,
        label: items.practice_name,
      }));
      setPracticeOptions(data);
    }
  };

  const getRoleList = async () => {
    const result = await getData(`${config.chartServerUrl}/all/role`, true);
    if (result) {
      const data = result.map((items) => ({
        value: items.role_id,
        label: items.role_name,
      }));
      setRoleOptions(data);
    }
  };

  const handleSelectChange = (selected) => {
    setSelectedOptions(selected);
  };

  const handleAddUser = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const formValues = Object.fromEntries(formData.entries());
    formValues.practice_ids = selectedOptions.map(option => option.value);

    try {
      const result = await postData(`${config.chartServerUrl}/store/user`, true, formValues);
      if(result.status === 'success'){
        notify('success', result.message);
        fetchData();
      } else {
        notify('error', result.message);
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    }
  }

  const editUser = async (id, name, email, practice, role, status) => {
    const userInformation = {
      user_id: id,
      practice_ids: practice,
      name: name,
      email: email,
      role_id: role,
      status: status
    };
    setUserEditInfo(userInformation);
    setUserEditOpen(true);
  };

  const handleEditUser = async (event) => {
    event.preventDefault();
    const formData = new FormData(event.target);
    const selectedPracticeIds = Array.from(formData.getAll('practice_ids'));

    const cleanedPracticeIds = selectedPracticeIds
      .map(id => parseInt(id, 10))
      .filter(id => !isNaN(id));

    const formValues = {
      ...Object.fromEntries(formData.entries()),
      practice_ids: cleanedPracticeIds
    };

    try {
      const result = await postData(`${config.chartServerUrl}/update/user`, true, formValues);
  
      if(result.status === 'success'){
        notify('success', result.message);
        fetchData();
      } else {
        notify('error', result.message);
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    }
  }

  return (
    <div>
      <Helmet>
        <title>Users | Medical Chart Management</title>
        <meta name="description" content="Users Page Description" />
      </Helmet>   
      
      <Header />

      <div className='container mx-auto mt-7 pb-28 px-2'>
        <div className="relative isolate" style={{ 'minHeight': '71vh' }}>

          <div className='flex items-center justify-between'>
            <h4 className='text-left uppercase font-bold text-xl text-gray-900 dark:text-gray-200'>Users</h4>
            <div className='flex items-center'>
              <Link to="/waiting-room" className='mr-2 font-semibold text-gray-900 dark:text-gray-200'>Chart</Link>
              <span className='text-xs'><ChevronRightIcon className="h-3 w-3 text-gray-500 dark:text-gray-200" /></span>
              <p className='ms-2 text-sm text-gray-600 dark:text-gray-300'>Users</p>
            </div>
          </div>

          <div className='mt-7 md:mt-12'>
            <div className='bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md'>
              <div className="border-b border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-4 py-4 sm:px-6">
                <div className="-ml-4 -mt-2 sm:flex flex-wrap items-center justify-between sm:flex-nowrap">
                  <div className="ml-4 mt-2">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-200">User List</h3>
                  </div>
                  <div className="ml-4 mt-2 flex-shrink-0">
                    <button type="button" onClick={() => setAddUserOpen(true)}
                      className="relative inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                    >
                      <PlusSmallIcon className="h-5 w-5 text-white mr-1" />Add User
                    </button>
                  </div>
                </div>
              </div>


              <div className="overflow-x-auto">
                <div className="inline-block min-w-full align-middle py-2 sm:px-4 lg:px-6">
                  <div className="overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-500">
                      <thead>
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-5  dark:text-gray-200">
                            Name
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900  dark:text-gray-200">
                            Email
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900  dark:text-gray-200">
                            Status
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900  dark:text-gray-200">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {allUsers ?
                          allUsers.map((user) => (
                              
                            <tr key={user.user_id}>
                              <td className="whitespace-nowrap py-2.5 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-5 dark:text-gray-200">{user.name}</td>
                              <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-500 dark:text-gray-200">{user.email}</td>
                              <td className="whitespace-nowrap px-3 py-2.5 text-sm text-gray-500 dark:text-gray-200">{user.status === 1 ? 'Active' : 'Inactive'}</td>
                              <td className="whitespace-nowrap px-3 py-2.5">
                                <button type="button" onClick={() => editUser(user.user_id, user.name, user.email, user.practice_ids, user.role_id, user.status)}
                                  className="rounded-md bg-indigo-50 dark:bg-gray-600 dark:text-gray-50 px-2 py-1.5 text-sm font-light text-indigo-600 shadow-sm hover:bg-indigo-100">
                                  <PencilSquareIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                            
                          ))
                        :
                          <tr>
                            <td colSpan={3} className="whitespace-nowrap text-center px-2 py-5 text-sm font-medium text-gray-900">No Data Found</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
        </div>
      </div>
      
      <Footer />

      {/* Add new User */}
      <Transition.Root show={addUserOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setAddUserOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full w-full sm:max-w-3xl">
                    <div className="bg-gray-100 dark:bg-gray-400 px-4 py-3 flex items-center justify-between">
                      <h4 className='text-xl font-bold dark:text-gray-100'>Add New User</h4>
                      <button type="button" className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                        onClick={() => setAddUserOpen(false)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                      </button>
                    </div>

                    <form onSubmit={handleAddUser} autoComplete='off'>
                      <div className="bg-white dark:bg-gray-300 px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Name </label>
                            <input type="text" name="name" placeholder="Name" required
                              className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:border-gray-50 dark:placeholder-gray-400 dark:text-gray-500 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                            />
                          </div>
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Email </label>
                            <input type="email" name="email" placeholder="Email" required
                              className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:border-gray-50 dark:placeholder-gray-400 dark:text-gray-500 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                            />
                          </div>
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Practice </label>
                            <Select
                              isMulti
                              name="practice_ids"
                              options={practiceOptions}
                              onChange={handleSelectChange}
                              placeholder="Select"
                              isSearchable={false}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (provided) => ({
                                  ...provided,
                                  zIndex: 9999,
                                }),
                                menu: (provided) => ({
                                  ...provided,
                                  fontSize: '14px',
                                  color: '#000',
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Role </label>
                            <Select
                              required
                              name="role_id"
                              options={roleOptions}
                              placeholder="Select"
                              isSearchable={false}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (provided) => ({
                                  ...provided,
                                  zIndex: 9999,
                                }),
                                menu: (provided) => ({
                                  ...provided,
                                  fontSize: '14px',
                                  color: '#000',
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </div>
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Password </label>
                            <input type="password" name="password" placeholder="Password" minLength={8} required onChange={handleInputChange}
                              className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:text-gray-500`}
                            />
                          </div>
                          <div className='col-span-2 sm:col-span-1'>
                            <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Confirm Password </label>
                            <input type="password" name="confirm_password" placeholder="Confirm Password" minLength={8} onChange={handleInputChange}
                              className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:text-gray-500`}
                            />
                          </div>
                          {!passwordsMatch && passwords.confirmTouched && (
                            <div className='col-span-2 -mt-3'>
                              <p className="text-red-500 text-sm">Passwords do not match.</p>
                            </div>
                          )}
                          <div className='col-span-2 mb-2'>
                            <div className='flex items-center'>
                              <p className="inline-block pb-1 sm:pb-0 text-sm font-normal text-gray-900 dark:text-gray-700 me-6">Status</p>
                              <div className="flex items-center space-x-10 ">
                                <div className="flex items-center">
                                  <input id='active' name="status" type="radio" value="1" defaultChecked className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" />
                                  <label htmlFor='active' className="ml-3 block text-sm leading-6 text-gray-900 cursor-pointer"> Active </label>
                                </div>
                                <div className="flex items-center">
                                  <input id='deactive' name="status" type="radio" value="2" className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600 cursor-pointer" />
                                  <label htmlFor='deactive' className="ml-3 block text-sm leading-6 text-gray-900 cursor-pointer "> Deactive </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-100 dark:bg-gray-400 px-4 py-3 flex flex-row-reverse sm:px-6">
                        <button type="submit" disabled={!passwordsMatch}
                          className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 ml-3 sm:w-auto"
                        >
                          Save
                        </button>
                        <button type="button" onClick={() => setAddUserOpen(false)}
                          className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </Dialog.Panel>
                </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Edit user */}
      <Transition.Root show={userEditOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setUserEditOpen}>
          <div className="fixed inset-0" />

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 bottom-0 top-auto">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-sm">
                    <div className="flex h-full flex-col bg-white dark:bg-gray-200 shadow-xl">
                      <div className="bg-indigo-700 px-4 py-4 sm:px-6">
                        <div className="flex justify-between">
                          <div className="">
                            <Dialog.Title className="text-base font-semibold leading-6 text-white">
                              Edit Information
                            </Dialog.Title>
                          </div>
                          <div className="ml-3 flex h-7 items-center">
                            <button type="button" onClick={() => setUserEditOpen(false)}
                              className="relative rounded-md bg-indigo-700 text-indigo-200 hover:text-white"
                            >
                              <span className="absolute -inset-2.5" />
                              <span className="sr-only">Close panel</span>
                              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="relative flex-1">
                        <form onSubmit={handleEditUser} autoComplete='off'>
                          <input type='hidden' name='user_id' defaultValue={userEditInfo ? userEditInfo.user_id : ''} />

                          <div className='overflow-y-scroll border-b dark:border-gray-300'>
                            <div className='px-5 pt-4 pb-5 border-b dark:border-gray-300'>
                              <div className='mb-2'>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900"> Name </label>
                                <input type="text" name="name" placeholder="Name" defaultValue={userEditInfo ? userEditInfo.name : ''}
                                  className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:border-gray-50 dark:placeholder-gray-400 dark:text-gray-500 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                                />
                              </div>
                              <div className='mb-2'>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900"> Email </label>
                                <input type="email" name='email' placeholder="Email" defaultValue={userEditInfo ? userEditInfo.email : ''}
                                  className="block outline-0 w-full rounded-md border-0 p-1.5 px-3 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-500 sm:text-sm sm:leading-6 dark:border-gray-50 dark:placeholder-gray-400 dark:text-gray-500 dark:focus:ring-indigo-500 dark:focus:border-indigo-500"
                                />
                              </div>
                              <div className='mb-2'>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Practice </label>
                                <Select
                                  isMulti
                                  defaultValue = {
                                    userEditInfo && userEditInfo?.practice_ids
                                      ? userEditInfo.practice_ids
                                        .slice(1, -1)
                                        .split(',')
                                        .map(id => {
                                          const foundOption = practiceOptions.find(option => option.value === parseInt(id));
                                          return foundOption ? foundOption : null;
                                        })
                                        .filter(Boolean)
                                      : []
                                  }
                                  name="practice_ids"
                                  options={practiceOptions}
                                  // onChange={handleSelectChange}
                                  placeholder="Search.."
                                  isSearchable={true}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      fontSize: '14px',
                                      color: '#000',
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>
                              <div className='mb-2'>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Role </label>
                                <Select
                                  required
                                  defaultValue = {
                                    userEditInfo
                                      ? roleOptions.find(option => option.value === userEditInfo.role_id)
                                      : null
                                  }
                                  name="role_id"
                                  options={roleOptions}
                                  placeholder="Search.."
                                  isSearchable={true}
                                  menuPortalTarget={document.body}
                                  styles={{
                                    menuPortal: (provided) => ({
                                      ...provided,
                                      zIndex: 9999,
                                    }),
                                    menu: (provided) => ({
                                      ...provided,
                                      fontSize: '14px',
                                      color: '#000',
                                      zIndex: 9999,
                                    }),
                                  }}
                                />
                              </div>

                              <div className='flex items-center pt-2'>
                                <p className="inline-block pb-1 sm:pb-0 text-sm font-normal text-gray-900 dark:text-gray-700 me-6">Status</p>
                                <div className="flex items-center space-x-10">
                                  <div className="flex items-center">
                                    <input id='active' name="status" type="radio" value="1" defaultChecked={userEditInfo && userEditInfo.status === 1} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                    <label htmlFor='active' className="ml-3 block text-sm leading-6 text-gray-900"> Active </label>
                                  </div>
                                  <div className="flex items-center m-0">
                                    <input id='deactive' name="status" type="radio" value="2" defaultChecked={userEditInfo && userEditInfo.status === 2} className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600" />
                                    <label htmlFor='deactive' className="ml-3 block text-sm leading-6 text-gray-900"> Deactive </label>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="px-5 pt-4 pb-5">
                              {/* <h3 className='font-semibold dark:text-gray-900 mb-2'>Profile Image</h3> */}
                              <div className='mb-2'>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Password </label>
                                <input type="password" name="password" placeholder="Password" minLength={8} onChange={handleInputChange}
                                  className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:text-gray-500`}
                                />
                              </div>
                              <div className=''>
                                <label className="inline-block pb-1 text-sm font-normal text-gray-900 dark:text-gray-700"> Confirm Password </label>
                                <input type="password" name="confirm_password" placeholder="Confirm Password" minLength={8} onChange={handleInputChange}
                                  className={`${passwords.matching ? 'ring-gray-300 focus:ring-indigo-500' : ''} ${passwordClasses} dark:text-gray-500`}
                                />

                                {!passwordsMatch && passwords.confirmTouched && (
                                  <p className="text-red-500 text-sm">Passwords do not match.</p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className='px-4 py-4 sm:px-6'>
                            <button type="submit"
                              className="w-full text-center relative inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                            >
                              <UserIcon className="h-4 w-4 text-white mr-2" />Update User
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      
    </div>
  );
};

export default AllUsers;
